import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw, AlertTriangle, RefreshCw, Users, UserRound } from "lucide-react";
import { API } from "@/lib/api";

const labelForType = (type) => type === "lead" ? "Lead" : "Kontakt";
const iconForType = (type) => type === "lead" ? <UserRound className="w-4 h-4" /> : <Users className="w-4 h-4" />;

export function CRMRecycleBinPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [permanentTarget, setPermanentTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/crm/recycle-bin`);
      setItems(response.data?.items || []);
    } catch (error) {
      setItems([]);
      toast.error(error.response?.data?.detail || "Papierkorb konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restore = async (item) => {
    setBusyId(item.id);
    try {
      const response = await axios.post(`${API}/crm/recycle-bin/${item.id}/restore`);
      toast.success(`${labelForType(response.data?.record_type || item.record_type)} wiederhergestellt`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Wiederherstellen fehlgeschlagen");
    } finally {
      setBusyId("");
    }
  };

  const permanentlyDelete = async () => {
    if (!permanentTarget) return;
    setBusyId(permanentTarget.id);
    try {
      await axios.delete(`${API}/crm/recycle-bin/${permanentTarget.id}`, { data: { confirm: true } });
      toast.success("Papierkorb-Eintrag endgültig entfernt");
      setPermanentTarget(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Endgültiges Löschen fehlgeschlagen");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-4" data-testid="crm-recycle-bin-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[var(--brand-ink)]">Papierkorb</h2>
          <p className="text-sm text-[var(--brand-muted)]">Hier liegen gelöschte Leads und Kontakte. Du kannst sie wiederherstellen oder bewusst endgültig entfernen.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="border-[var(--brand-border)]" data-testid="crm-recycle-bin-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </Button>
      </div>

      <Card className="border-[var(--brand-border)]">
        {loading ? <p className="p-6 text-sm text-[var(--brand-muted)]">Papierkorb lädt …</p> : items.length === 0 ? (
          <div className="p-8 text-center">
            <Trash2 className="w-7 h-7 text-[var(--brand-muted)] mx-auto mb-2" />
            <p className="text-sm text-[var(--brand-muted)]" data-testid="crm-recycle-bin-empty">Der Papierkorb ist leer.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--brand-border-light)]">
            {items.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`crm-recycle-bin-row-${item.id}`}>
                <div className="text-[var(--brand-muted)]">{iconForType(item.record_type)}</div>
                <div className="min-w-[180px] flex-1">
                  <div className="text-sm font-medium text-[var(--brand-ink)]">{item.label}</div>
                  <div className="text-xs text-[var(--brand-muted)]">{labelForType(item.record_type)} · gelöscht am {new Date(item.deleted_at).toLocaleString("de-DE")}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(item)} disabled={busyId === item.id} className="border-[var(--brand-border)]" data-testid={`crm-recycle-bin-restore-${item.id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Wiederherstellen
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPermanentTarget(item)} disabled={busyId === item.id} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`crm-recycle-bin-permanent-delete-${item.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Endgültig löschen
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {permanentTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPermanentTarget(null)}>
          <Card className="w-full max-w-md bg-white p-5" onClick={(event) => event.stopPropagation()} data-testid="crm-recycle-bin-permanent-confirm">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-[var(--brand-ink)]">Endgültig löschen?</h3>
                <p className="mt-2 text-sm text-[var(--brand-muted)]">„{permanentTarget.label}“ wird endgültig aus dem Papierkorb entfernt. Diese Aktion kann nicht rückgängig gemacht werden.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPermanentTarget(null)} data-testid="crm-recycle-bin-permanent-cancel">Abbrechen</Button>
              <Button onClick={permanentlyDelete} disabled={busyId === permanentTarget.id} className="bg-red-600 hover:bg-red-700 text-white" data-testid="crm-recycle-bin-permanent-confirm-btn">Endgültig löschen</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
